import { initContract } from "@ts-rest/core";
import { z } from "zod";
/**
 * Dashboard — the Übersicht (Overview) aggregate (ADR-033) + the pages behind its cards (ADR-034): the caller's
 * visible manuals as ONE light list (Manuals / Favourite manuals / Ratings sort, filter and group it client-side,
 * as the legacy did), the task ROWS the overview only counts, and the keyset-paginated news feed. Everything is
 * computed SERVER-SIDE over the caller's VISIBLE manuals (the same predicate as every manual list — the dashboard
 * never reveals a manual the browse screen would hide). The profile card reads `/me`.
 */
const c = initContract();
const UnauthorizedSchema = z.object({ message: z.string() });
const ManualStateSchema = z.enum(["draft", "ready_for_review", "confirmed", "marked"]);
/** The review state-machine actions the Verlauf (History) card shows — status changes only (owner, ADR-033 §6 #3). */
export const DashboardNewsActionSchema = z.enum(["submit_review", "confirm", "mark", "unmark", "return_to_draft"]);
export const DashboardNewsItemSchema = z.object({
    id: z.number().int(),
    manualId: z.number().int(),
    manualName: z.string(),
    facilityId: z.number().int(),
    facilityName: z.string(),
    action: DashboardNewsActionSchema,
    /** The actor's name snapshot at action time (never `sub`/roles); null for ETL rows without one. */
    actorName: z.string().nullable(),
    createdAt: z.string(), // ISO-8601 UTC
    /** The report text of a `mark` (server-sanitized plain text — render as a TEXT node); null for every other action. */
    comment: z.string().nullable(),
});
/** One page of the Verlauf (History) feed — keyset-paginated, newest first; `nextCursor` is opaque (ADR-034 D1). */
export const DashboardNewsPageSchema = z.object({ items: z.array(DashboardNewsItemSchema), nextCursor: z.string().nullable() });
/**
 * A manual on the Dashboard's Manuals / Favourite manuals / Ratings pages (ADR-034 D1): the card's scalars, its
 * facility location, and the caller-relative flags the legacy filters need. NO description (the drawer has it).
 */
export const DashboardManualSchema = z.object({
    id: z.number().int(),
    name: z.string(),
    state: ManualStateSchema,
    isArManual: z.boolean(),
    /** Set when this manual is a LINKED child (the legacy "linked" badge). */
    parentManualId: z.number().int().nullable(),
    facilityId: z.number().int(),
    facilityName: z.string(),
    /** The facility's ltree path (ids) — the web renders the breadcrumb from its loaded tree. */
    facilityPath: z.string(),
    categoryName: z.string(),
    createdAt: z.string(), // ISO-8601 UTC
    updatedAt: z.string(),
    creatorUsername: z.string().nullable(),
    lastUpdatedByUsername: z.string().nullable(),
    /** The manual itself is archived (`?archived=true` only). */
    archived: z.boolean(),
    /** The manual's FACILITY is archived (`?archived=true` only) — the legacy's "Facility archived" row; it does not navigate. */
    facilityArchived: z.boolean(),
    assignedToMe: z.boolean(),
    createdByMe: z.boolean(),
    /** When the caller favourited it (ISO-8601 UTC); null when not a favourite. The Favourite manuals page = this !== null. */
    favouritedAt: z.string().nullable(),
    /** The manual's ratings: count + average (1..5); null when unrated. */
    rating: z.object({ count: z.number().int().positive(), average: z.number() }).nullable(),
});
export const DashboardManualsSchema = z.object({ manuals: z.array(DashboardManualSchema) });
/** The hint engine's kinds (`dashboard-hints.ts`) — a hint (bell) or a warning (triangle) per manual. */
export const ManualTaskTypeSchema = z.enum(["ManualInDraft", "ManualReadyForReview", "InReviewLongerThanOneWeek", "ManualConfirmedAndMarked"]);
/** The legacy's two role warnings (Manage roles: no level set / no facility granted). */
export const RoleTaskTypeSchema = z.enum(["NoHighestFacilityPermissionSet", "NoFacilityPermissionsSet"]);
export const DashboardTaskKindSchema = z.enum(["hint", "warning"]);
export const ManualTaskSchema = z.object({
    manualId: z.number().int(),
    manualName: z.string(),
    facilityId: z.number().int(),
    facilityName: z.string(),
    kind: DashboardTaskKindSchema,
    type: ManualTaskTypeSchema,
});
export const RoleTaskSchema = z.object({ roleId: z.number().int(), roleName: z.string(), type: RoleTaskTypeSchema });
/** The Tasks page (ADR-034 D1): the ROWS whose counts the overview card shows — the same engine, the same scope. */
export const DashboardTasksSchema = z.object({ manuals: z.array(ManualTaskSchema), roles: z.array(RoleTaskSchema) });
export const DashboardOverviewSchema = z.object({
    /** Counts over the caller's VISIBLE, live (not archived, not deleted) manuals. `released` = confirmed + marked. */
    manuals: z.object({
        total: z.number().int().nonnegative(),
        draft: z.number().int().nonnegative(),
        review: z.number().int().nonnegative(),
        released: z.number().int().nonnegative(),
    }),
    /** The legacy hint engine's COUNTS for the caller (ADR-033 §2.3): hints (bell) and warnings (triangle), incl. the
     *  role warnings for a caller admitted to Manage roles. The Tasks page lists them. */
    tasks: z.object({ hints: z.number().int().nonnegative(), warnings: z.number().int().nonnegative() }),
    /** Ratings over the visible rated manuals: the number of ratings + the mean of the per-manual averages (the
     *  legacy's Ø); null when no visible manual has a rating. */
    ratings: z.object({ count: z.number().int().positive(), average: z.number() }).nullable(),
    /** The newest 3 status changes over visible manuals (a deleted manual's rows are skipped — nothing to open). */
    news: z.array(DashboardNewsItemSchema).max(3),
});
export const dashboardContract = c.router({
    getOverview: {
        method: "GET",
        path: "/dashboard/overview",
        responses: { 200: DashboardOverviewSchema, 401: UnauthorizedSchema },
        summary: "The dashboard Übersicht aggregate: manual counts by state, hint/warning counts, ratings, the newest 3 status changes — over the caller's visible manuals (ADR-033).",
    },
    listManuals: {
        method: "GET",
        path: "/dashboard/manuals",
        query: z.object({
            // A STRING enum over the querystring (see `listFacilityManuals`). `"true"` → the manuals that are archived
            // themselves OR sit under an archived facility (the legacy "show archived" switch); `"false"` → the live ones.
            archived: z.enum(["true", "false"]).optional().default("false"),
        }),
        responses: { 200: DashboardManualsSchema, 401: UnauthorizedSchema },
        summary: "Every manual the caller can see, as a light card with the caller-relative flags (ADR-034 D1) — the Manuals / Favourite manuals / Ratings pages filter it client-side.",
    },
    getTasks: {
        method: "GET",
        path: "/dashboard/tasks",
        responses: { 200: DashboardTasksSchema, 401: UnauthorizedSchema },
        summary: "The Tasks page rows: the hint engine's hints + warnings per visible manual, and the role warnings of the caller's Manage-roles scope (ADR-034).",
    },
    getNews: {
        method: "GET",
        path: "/dashboard/news",
        query: z.object({
            limit: z.coerce.number().int().min(1).max(100).optional().default(50),
            /** The previous page's `nextCursor` — opaque; a malformed cursor yields the first page. */
            before: z.string().max(64).optional(),
        }),
        responses: { 200: DashboardNewsPageSchema, 401: UnauthorizedSchema },
        summary: "The History page: status changes over visible manuals, newest first, keyset-paginated (ADR-034).",
    },
});
//# sourceMappingURL=dashboard.contract.js.map